import {
    useQuery,
    useMutation,
    useInfiniteQuery,
    useQueryClient,
} from '@tanstack/react-query';

import {useClient} from 'context/auth-context';
import {
    PENDING_TRANSACTIONS_CACHE,
    TRANSACTION_DETAILS_CACHE,
    TRANSACTIONS_CACHE,
    PAYMENT_FILES_CACHE,
    PAYMENT_FILE_DETAILS_CACHE,
    MULTI_PAYMENT,
    MULTI_PAYMENT_TRANSACTIONS,
    PAYOUT_TRACKING_CACHE,
    PENDING_TRANSACTIONS_DETAILS_LIMIT_ORDERDS_CACHE,
    TRANSACTIONS_TAG_REPORT_CACHE,
    AUTOMATION,
    AUTOMATION_INFINITE,
    CARD_TRANSACTIONS_DETAILS_CACHE,
    CARD_TRANSACTION_PUBLIC_CACHE,
} from 'constants/cache';
import {ONE_HUNDRED, PAGINATION_LIMIT} from 'constants/common';
import {FxTab} from 'types/order';
import {
    ICardTransactionDetail,
    ICardTransactionDetailsPublic,
    IFxTransaction,
    IFxTransactionDetail,
    Transaction,
    TransactionDetails,
} from 'types/transactions';
import {IDocument} from 'types/documents';
import {IAmnisApiFilters} from 'components/ui/AmnisFilters/types';
import {BusinessPartner} from 'types/api/business-partners';
import {PATCH_HEADERS} from './constants';

export interface IUseGetInfiniteFxTransactionsRequest {
    tab: FxTab;
    itemsCount?: number;
    filters?: IAmnisApiFilters;
}
export interface IUseGetTransactionsForTagAggregationsResponse {
    inflows: {[key: string]: number};
    outflows: {[key: string]: number};
    net: {[key: string]: number};
}

export interface IUseGetTransactionsForTagResponse {
    '@context': '/contexts/Transaction';
    '@id': '/web_api/transactions';
    '@type': 'hydra:Collection';
    'hydra:member': Transaction[];
    'hydra:aggregations': IUseGetTransactionsForTagAggregationsResponse;
    'hydra:totalItems': number;
}

export interface IUseGetInfiniteFxTransactionsResponse {
    '@context': '/web_api/contexts/FxTransaction';
    '@id': '/web_api/transactions';
    '@type': 'hydra:Collection';
    'hydra:member': IFxTransaction[];
    'hydra:totalItems': number;
}

export interface IUseGetInfiniteFxTransactionsDetailsResponse {
    '@context': '/web_api/contexts/FxTransactionDetail';
    '@id': '/web_api/fx_transaction_details';
    '@type': 'hydra:Collection';
    'hydra:member': IFxTransactionDetail[];
    'hydra:totalItems': number;
}

const TRANSACTIONS_ENDPOINT = '/web_api/transactions';

const getFxTypeQueryParam = (tab: FxTab) => {
    if (FxTab.HISTORY !== tab) {
        if (FxTab.NO_LIMIT === tab) {
            return {'fxTransactionDetail.dealRequest.type[neq]': 'limit'};
        }
        return {'dealRequest.type': 'limit'};
    }
    return null;
};

const useGetInfiniteFxTransactions = ({
    tab,
    itemsCount = PAGINATION_LIMIT,
    filters,
}: IUseGetInfiniteFxTransactionsRequest) => {
    const client = useClient();
    const executed = tab !== FxTab.HISTORY ? 0 : 1;
    const order = tab !== FxTab.HISTORY ? 'ASC' : 'DESC';

    const {currencyFrom, currencyTo, transactionDate, valueDate} =
        filters || {};

    return useInfiniteQuery<IUseGetInfiniteFxTransactionsResponse>({
        queryKey: [
            PENDING_TRANSACTIONS_CACHE,
            {itemsCount, executed, order, filters, tab},
        ],
        queryFn: ({pageParam}) => {
            return client(
                TRANSACTIONS_ENDPOINT,
                {
                    params: {
                        itemsPerPage: itemsCount.toString(),
                        executed: executed.toString(),
                        'order[executionDate]': order,
                        'order[id]': 'DESC',
                        'amount[gt]': '0',
                        ...getFxTypeQueryParam(tab),
                        transaction_type: 'fx_transaction',
                        'fxTransactionDetail.currencyFrom': currencyFrom,
                        'fxTransactionDetail.currencyTo': currencyTo,
                        'fxTransactionDetail.valueDate': valueDate,
                        'fxTransactionDetail.dealDate[after]': transactionDate,
                        'fxTransactionDetail.valueDate[before]': valueDate,
                        page: pageParam,
                    },
                },
                true,
            );
        },
        initialPageParam: 1,
        getNextPageParam: (lastGroup, allGroups) => {
            if (
                lastGroup['hydra:member'].length === itemsCount &&
                allGroups.length * itemsCount < lastGroup['hydra:totalItems']
            ) {
                return allGroups.length + 1;
            }
            return undefined;
        },
    });
};

const useGetInfiniteFxTransactionsDetailsForPendingLimitOrders = ({
    tab,
    itemsCount = PAGINATION_LIMIT,
    filters,
}: IUseGetInfiniteFxTransactionsRequest) => {
    const client = useClient();

    return useInfiniteQuery<IUseGetInfiniteFxTransactionsDetailsResponse>({
        queryKey: [
            PENDING_TRANSACTIONS_DETAILS_LIMIT_ORDERDS_CACHE,
            {tab, filters},
        ],
        queryFn: ({pageParam}) => {
            return client(
                `/web_api/fx_transaction_details?`,
                {
                    params: {
                        page: pageParam,
                        itemsPerPage: itemsCount,
                        status: 'pending',
                        'order[valueDate]': 'ASC',
                        'dealRequest.type': 'limit',
                        ...filters,
                    },
                },
                true,
            );
        },
        initialPageParam: 1,
        getNextPageParam: (lastGroup, allGroups) => {
            if (
                lastGroup['hydra:member'].length === itemsCount &&
                allGroups.length * itemsCount < lastGroup['hydra:totalItems']
            ) {
                return allGroups.length + 1;
            }
            return undefined;
        },
    });
};

const useGetTransactionDetails = (
    id: string | null,
    withTransaction?: boolean,
) => {
    const client = useClient();

    return useQuery<Transaction | TransactionDetails>({
        queryKey: [TRANSACTION_DETAILS_CACHE, {id}],
        queryFn: () =>
            client(id!, {
                params: {
                    withTransaction: withTransaction ? 1 : undefined,
                    groups: ['ExternalIntegrationsView'],
                },
            }),
        enabled: !!id,
    });
};

const useGetCardTransactionDetailsPublic = (hash: string) => {
    const client = useClient();

    return useQuery<ICardTransactionDetailsPublic>({
        queryKey: [CARD_TRANSACTION_PUBLIC_CACHE, {hash}],
        queryFn: () =>
            client(`/web_api/card_transaction_details_public/${hash}`, {
                params: {
                    groups: ['ExternalIntegrationsView'],
                },
            }),
        enabled: !!hash,
    });
};

const useGetTransactionDetailsByType = (query: string) => {
    const client = useClient();

    return useQuery({
        queryKey: [TRANSACTION_DETAILS_CACHE, {id: query}],
        queryFn: () => client(query, {}),
        enabled: !!query,
    });
};

const useGetTransactionsList = (
    id: string,
    page: number,
    filters: IAmnisApiFilters,
    businessPartner: BusinessPartner,
    itemsCount: number = PAGINATION_LIMIT,
) => {
    const client = useClient();

    return useQuery<Transaction[]>({
        queryKey: [TRANSACTIONS_CACHE, {filters, page}],
        queryFn: () =>
            client(TRANSACTIONS_ENDPOINT, {
                params: {
                    businessPartner: businessPartner['@id'],
                    page,
                    itemsPerPage: itemsCount,
                    ...filters,
                },
            }),
        enabled: !!id,
    });
};

const useGetTransactionsListForTag = (
    id: string,
    filters: IAmnisApiFilters,
    pagination = false,
) => {
    const client = useClient();

    const {
        executionDate,
        currency,
        'card.cardHolder': cardHolder,
        card,
        contact,
        'card.name': cardLabel,
        'card.embossedName': nameOnCard,
        tags,
        name,
        ...rest
    } = filters;

    const {after, before} = executionDate || {};

    return useQuery<{
        transactions: Transaction[];
        flows: {
            inflows: {[key: string]: number};
            outflows: {[key: string]: number};
            net: {[key: string]: number};
        };
    }>({
        queryKey: [TRANSACTIONS_TAG_REPORT_CACHE, {filters}],
        queryFn: async () => {
            const response = await client(
                `${TRANSACTIONS_ENDPOINT}?pagination=${pagination}`,
                {
                    params: {
                        ...rest,
                        itemsPerPage: ONE_HUNDRED,
                        executionDate: before || after ? {after, before} : null,
                        currency,
                        'cardTransactionDetail.card': card,
                        'cardTransactionDetail.card.name': cardLabel,
                        'cardTransactionDetail.card.embossedName': nameOnCard,
                        'cardTransactionDetail.merchant.name': name,
                        contact,
                        tags,
                    },
                },
                true,
            );
            const flows = (response as any)['hydra:aggregations']; // TODO
            const sortedFlows: IUseGetTransactionsForTagAggregationsResponse = {
                inflows: {},
                outflows: {},
                net: {},
            };
            Object.keys(flows).forEach(flowKey => {
                (sortedFlows as any)[flowKey] = Object.keys(flows[flowKey])
                    .sort()
                    .reduce(
                        (acc, key) => ({
                            ...acc,
                            [key]: flows[flowKey][key],
                        }),
                        {},
                    );
            });
            return {
                transactions: (response as any)['hydra:member'],
                flows: sortedFlows,
            };
        },
        enabled:
            !!id &&
            !!tags?.length &&
            !!executionDate?.before &&
            !!executionDate?.after,
    });
};

export interface IUseGetInfiniteTransactionListResponse {
    '@context': string;
    '@id': string;
    '@type': string;
    'hydra:member': Transaction[];
    'hydra:totalItems': number;
}

export interface IUseGetInfiniteCardTransactionDetailsListResponse {
    '@context': string;
    '@id': string;
    '@type': string;
    'hydra:member': ICardTransactionDetail[];
    'hydra:totalItems': number;
}

const useGetInfiniteTransactionList = (
    filters: IAmnisApiFilters,
    businessPartner: BusinessPartner,
    itemsCount = PAGINATION_LIMIT,
    bpId?: number,
) => {
    const client = useClient();

    const {
        'card.cardHolder': cardHolder,
        card,
        merchant,
        source,
        ...rest
    } = filters;
    return useInfiniteQuery<IUseGetInfiniteTransactionListResponse>({
        queryKey: [TRANSACTIONS_CACHE, {filters, bpId}],
        queryFn: ({pageParam}) => {
            return client(
                TRANSACTIONS_ENDPOINT,
                {
                    params: {
                        businessPartner: businessPartner['@id'],
                        page: pageParam,
                        itemsPerPage: itemsCount,
                        ...rest,
                        'cardTransactionDetail.card.cardHolder': cardHolder,
                        'cardTransactionDetail.card': card,
                        'cardTransactionDetail.merchant': merchant,
                        'cardTransactionDetail.source': source,
                        'order[id]': 'DESC',
                    },
                },
                true,
            );
        },
        initialPageParam: 1,
        getNextPageParam: (lastGroup, allGroups) => {
            if (
                lastGroup['hydra:member'].length === itemsCount &&
                allGroups.length * itemsCount < lastGroup['hydra:totalItems']
            ) {
                return allGroups.length + 1;
            }
            return undefined;
        },
    });
};

const useGetInfiniteCardTransactionDetailsList = (
    filters: IAmnisApiFilters & {groups?: string[]},
    itemsCount = PAGINATION_LIMIT,
    bpId: number,
    cardGroupIri?: string | null,
    cardIri?: string,
) => {
    const client = useClient();

    const {
        executionDate,
        currency,
        card,
        merchant,
        cardTransactionStatus,
        source,
        hasDocument,
        ...rest
    } = filters;
    const {before, after} = executionDate || {};

    return useInfiniteQuery<IUseGetInfiniteCardTransactionDetailsListResponse>({
        queryKey: [CARD_TRANSACTIONS_DETAILS_CACHE, {filters, bpId, cardIri}],
        queryFn: ({pageParam}) => {
            return client(
                '/web_api/card_transaction_details',
                {
                    params: {
                        ...rest,
                        page: pageParam,
                        itemsPerPage: itemsCount,
                        executionDate: before || after ? {after, before} : null,
                        currency,
                        cardGroup: cardGroupIri,
                        card: cardIri || card,
                        status: cardTransactionStatus,
                        merchant,
                        'order[executionDate]': 'DESC',
                        source,
                        hasDocument,
                    },
                },
                true,
            );
        },
        initialPageParam: 1,
        getNextPageParam: (lastGroup, allGroups) => {
            if (
                lastGroup['hydra:member'].length === itemsCount &&
                allGroups.length * itemsCount < lastGroup['hydra:totalItems']
            ) {
                return allGroups.length + 1;
            }
            return undefined;
        },
    });
};

export interface IUseUploadTransactionDetailFileRequest {
    transactionDetailIri: string;
    file: File;
}

const useUploadTransactionDetailFile = () => {
    const client = useClient();

    return useMutation<
        IDocument,
        unknown,
        IUseUploadTransactionDetailFileRequest
    >({
        mutationFn: ({transactionDetailIri, file}) => {
            const formData = new FormData();
            formData.append('file', file);

            return client(`${transactionDetailIri}/expense_upload`, {
                data: formData,
                method: 'POST',
                headers: {'Content-Type': 'multipart/form-data'},
                keepOriginal: true,
            });
        },
    });
};

export interface IUseUploadCardTransactionDetailFilePublicRequest {
    hash: string;
    file: File;
}

const useUploadCardTransactionDetailFilePublic = () => {
    const client = useClient();

    return useMutation<
        IDocument,
        unknown,
        IUseUploadCardTransactionDetailFilePublicRequest
    >({
        mutationFn: ({hash, file}) => {
            const formData = new FormData();
            formData.append('file', file);

            return client(
                `/web_api/card_transaction_details_public/${hash}/expense_upload`,
                {
                    data: formData,
                    method: 'POST',
                    headers: {'Content-Type': 'multipart/form-data'},
                    keepOriginal: true,
                },
            );
        },
    });
};

const useSignTransaction = () => {
    const client = useClient();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string) => {
            return client(`${id}/sign`, {
                data: {},
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/vnd.api+json',
                },
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_FILES_CACHE],
            });
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_FILE_DETAILS_CACHE],
            });
            queryClient.invalidateQueries({queryKey: [TRANSACTIONS_CACHE]});
            queryClient.invalidateQueries({
                queryKey: [TRANSACTION_DETAILS_CACHE],
            });
            queryClient.invalidateQueries({queryKey: [AUTOMATION]});
            queryClient.invalidateQueries({
                queryKey: [AUTOMATION_INFINITE],
            });
        },
    });
};

const useUpdateTransaction = () => {
    const client = useClient();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (request: {transactionId: string; data: any}) =>
            client(`${TRANSACTIONS_ENDPOINT}/${request.transactionId}`, {
                data: request.data,
                method: 'PATCH',
                ...PATCH_HEADERS,
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_FILES_CACHE],
            });
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_FILE_DETAILS_CACHE],
            });
            queryClient.invalidateQueries({queryKey: [TRANSACTIONS_CACHE]});
            queryClient.invalidateQueries({queryKey: [MULTI_PAYMENT]});
            queryClient.invalidateQueries({
                queryKey: [MULTI_PAYMENT_TRANSACTIONS],
            });
            queryClient.invalidateQueries({
                queryKey: [TRANSACTION_DETAILS_CACHE],
            });
        },
    });
};

const useUpdateTransactionDetail = () => {
    const client = useClient();
    const queryClient = useQueryClient();

    return useMutation<
        TransactionDetails,
        unknown,
        Partial<TransactionDetails & {tags: string[]}>
    >({
        mutationFn: request =>
            client(request['@id']!, {
                data: request,
                method: 'PATCH',
                ...PATCH_HEADERS,
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: [TRANSACTIONS_CACHE]});
            queryClient.invalidateQueries({
                queryKey: [TRANSACTION_DETAILS_CACHE],
            });
        },
    });
};

const useDeleteTransaction = () => {
    const client = useClient();
    const queryClient = useQueryClient();

    return useMutation<{}, unknown, string>({
        mutationFn: id =>
            client(`${id}`, {
                data: {},
                method: 'DELETE',
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_FILES_CACHE],
            });
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_FILE_DETAILS_CACHE],
            });
            queryClient.invalidateQueries({queryKey: [TRANSACTIONS_CACHE]});
            queryClient.invalidateQueries({queryKey: [MULTI_PAYMENT]});
            queryClient.invalidateQueries({
                queryKey: [MULTI_PAYMENT_TRANSACTIONS],
            });
        },
    });
};

export interface ITracking {
    transaction_status: string;
    transaction_status_reason: string | null;
    initiation_time: string;
    completion_time: string;
    last_update_time: string;
    events: ITrackingEvent[];
    nbr_events: number;
}

export interface ITrackingEvent {
    tracker_event_type: string;
    last_update_time: string;
    charge_type: null | string;
    from_bic: string;
    from_name: string;
    to_bic: string;
    to_name: string;
    confirmed?: ITrackingEventConfirm;
    instructed?: ITrackingEventConfirm;
}

export interface ITrackingEventConfirm {
    amount: string;
    currency: string;
}

const useGetPayoutTrackingInfo = (
    payoutTransactionDetailsId?: string | null,
    enabled?: boolean,
) => {
    const client = useClient();

    return useQuery<ITracking>({
        queryKey: [PAYOUT_TRACKING_CACHE, {id: payoutTransactionDetailsId}],
        queryFn: () => client(`${payoutTransactionDetailsId}/tracking`, {}),
        enabled: !!payoutTransactionDetailsId && enabled,
    });
};

const useBatchSignTransactions = () => {
    const client = useClient();

    return useMutation<unknown, unknown, string[]>({
        mutationFn: transactions =>
            client('/web_api/transaction_details/sign', {
                method: 'POST',
                data: {transactions},
            }),
    });
};

interface IUseUploadTransactionFile {
    transactionIri: string;
    file: File;
}

export {
    useUpdateTransactionDetail,
    useGetTransactionDetails,
    useGetTransactionDetailsByType,
    useGetTransactionsList,
    useGetTransactionsListForTag,
    useGetInfiniteCardTransactionDetailsList,
    useSignTransaction,
    useUpdateTransaction,
    useDeleteTransaction,
    useGetInfiniteTransactionList,
    useGetInfiniteFxTransactions,
    useGetInfiniteFxTransactionsDetailsForPendingLimitOrders,
    useGetPayoutTrackingInfo,
    useUploadTransactionDetailFile,
    useUploadCardTransactionDetailFilePublic,
    useGetCardTransactionDetailsPublic,
    useBatchSignTransactions,
};
